// ============================================================================
// L'ANALYSE DU FICHIER AVANT L'IMPORT — ce que l'étape « Rapport » de l'assistant montre avant de lancer.
//
// UTILITÉ : appliquer le masque aux lignes du fichier, et dire AVANT d'envoyer ce qui n'entrera pas : un code-barres
// mal formé, une désignation vide, un prix absent ou illisible, une quantité illisible, une ligne répétée. Le serveur
// refera ces contrôles (c'est lui qui écrit) ; ici on les fait pour que l'opérateur les voie avant de cliquer, avec le
// numéro de ligne du fichier — le numéro qu'il retrouvera dans son tableur.
//
// ⚠️ UNE LIGNE SANS CODE-BARRES N'EST PAS ÉCARTÉE (décision utilisateur) : le serveur la rapproche d'un article
//    existant par code article, couleur et taille, ou lui attribue un gencode interne. Elle est COMPTÉE à part, pour
//    que l'écran le dise avant de lancer.
// ⚠️ ELLE NE TRANSFORME AUCUNE VALEUR : le masque dit quelle colonne alimente quel champ, l'analyse recopie. Le prix
//    « 69,90 » part tel quel, c'est le serveur qui le lit — une seule règle de lecture, pas deux qui divergent.
// ⚠️ LES DOUBLONS SE JUGENT COMME AU SERVEUR : par le code-barres quand la ligne en porte un, par la déclinaison
//    (code article — celui du fichier ou celui proposé —, couleur, taille, seconde dimension) sinon.
// ============================================================================
import { isValidEan13, parseCellQuantity, proposeArticleCode } from '@orgazone/core-catalog';
import { currentCurrency, parseCellAmount } from '@orgazone/money-format';
import { normalizeCode } from '@orgazone/sync-contract';
import type { ImportArticleRow } from '../api';
import type { Masque } from './import-masque';
import { t } from '../i18n';

/** Une ligne écartée par l'analyse : son numéro dans le fichier, le motif, un extrait pour la retrouver. */
export interface LigneEcartee { line: number; why: string; ex: string }
/** Un doublon interne au fichier : la ligne écartée, et ce qu'elle répète. */
export interface DoublonFichier { line: number; ex: string }

export interface AnalyseImport {
  /** Les lignes PRÊTES à partir, sous leur nom de champ. */
  rows: ImportArticleRow[];
  bad: LigneEcartee[];
  dup: DoublonFichier[];
  /** Les lignes lues dans le fichier (hors en-tête). */
  read: number;
  /** Combien de lignes prêtes portent une quantité. */
  withQty: number;
  /** Combien de lignes prêtes n'ont pas de code-barres — rapprochées ou dotées d'un gencode interne au serveur. */
  withoutBarcode: number;
  /** Les familles du fichier, une fois chacune, dans l'ordre où elles apparaissent. */
  families: string[];
}

/** Un champ TEXTE de la ligne — tout sauf le numéro de ligne. */
type ChampTexte = Exclude<keyof ImportArticleRow, 'line'>;
/** Clé de masque (celle du dessin) → champ de l'import. */
const CHAMP: Record<string, ChampTexte> = {
  gencode: 'gencode', n: 'name', code: 'code', fam: 'family', coul: 'color', taille: 'size', long: 'size2',
  pv: 'price', pa: 'cost', marq: 'brand', sect: 'sector', tva: 'vat', sais: 'season', web: 'web', qte: 'qty',
};

/** Ce qu'une douchette a le droit de rendre — la même règle que le serveur : 8 à 14 chiffres, et à 13 la clé doit être juste. */
const gencodeOk = (g: string): boolean => /^\d{8,14}$/.test(g) && (g.length !== 13 || isValidEan13(g));
// ⚠️ PRIX ET QUANTITÉS SE LISENT PAR LES LECTEURS DU SERVEUR (`parseCellAmount`, `parseCellQuantity`), à l'échelle
//    de la devise du magasin : une règle écrite ici aurait annoncé « 1 200 lignes à importer » pour un fichier dont le
//    serveur refuse les « 1 299,00 » ou les « 12,999 » — l'écran promettait ce que l'envoi ne tenait pas.

/** L'identité d'une déclinaison sans code-barres — la règle même du serveur (`declinationKey`). */
const cleDeDeclinaison = (row: ImportArticleRow): string =>
  [row.code ?? proposeArticleCode(row.name ?? ''), row.color ?? '', row.size ?? '', row.size2 ?? ''].map((v) => normalizeCode(v)).join('|');

/**
 * APPLIQUE le masque et TRIE les lignes : prêtes, écartées, doublons.
 * ⚠️ LA NUMÉROTATION EST CELLE DU FICHIER (l'en-tête compte pour la ligne 1) : un « L37 » doit se retrouver en ligne
 *    37 du tableur, sinon le rapport envoie corriger la mauvaise ligne.
 */
export function analyserImport(lignes: readonly (readonly string[])[], masque: Masque, hasHeading: boolean, numeros?: readonly number[]): AnalyseImport {
  const out: AnalyseImport = { rows: [], bad: [], dup: [], read: 0, withQty: 0, withoutBarcode: 0, families: [] };
  const colonnes = Object.entries(masque).map(([i, k]) => [Number(i), k] as const).filter(([, k]) => k !== 'skip' && k in CHAMP);
  const vus = new Set<string>();
  const familles = new Set<string>();
  lignes.forEach((cells, idx) => {
    if (hasHeading && idx === 0) return;
    if (cells.every((c) => (c ?? '').trim() === '')) return;
    out.read += 1;
    // ⚠️ LE NUMÉRO DU TABLEUR quand le lecteur le donne : ses lignes vides retirées ne décalent pas le rapport.
    const line = numeros?.[idx] ?? idx + 1;
    const row: ImportArticleRow = { line };
    for (const [i, k] of colonnes) {
      const v = (cells[i] ?? '').trim();
      if (v) row[CHAMP[k]!] = v;
    }
    const extrait = [row.gencode, row.name].filter(Boolean).join(' · ') || cells.slice(0, 3).join(' · ');
    if (row.gencode && !gencodeOk(row.gencode)) { out.bad.push({ line, why: t('Code-barres mal formé (8 à 14 chiffres, clé EAN-13 juste)'), ex: extrait }); return; }
    // ⚠️ LES MOTIFS ET LES EXTRAITS DE LA MAQUETTE : une désignation vide montre le code-barres, un prix illisible
    //    montre ce qui a été lu dans sa cellule — c'est ce qu'on cherche dans le tableur pour corriger.
    if (!row.name) { out.bad.push({ line, why: t('Désignation vide'), ex: row.gencode ?? '—' }); return; }
    if (row.price === undefined || parseCellAmount(row.price, currentCurrency().scale) === null) { out.bad.push({ line, why: t('Prix illisible'), ex: (row.price ?? '—').slice(0, 18) }); return; }
    if (row.qty !== undefined && parseCellQuantity(row.qty) === null) { out.bad.push({ line, why: t('Quantité illisible : « {qty} »', { qty: row.qty }), ex: extrait }); return; }
    const identite = row.gencode ?? `k:${cleDeDeclinaison(row)}`;
    if (vus.has(identite)) { out.dup.push({ line, ex: extrait }); return; }
    vus.add(identite);
    if (row.qty && Number(row.qty) > 0) out.withQty += 1;
    if (!row.gencode) out.withoutBarcode += 1;
    if (row.family && !familles.has(normalizeCode(row.family))) { familles.add(normalizeCode(row.family)); out.families.push(row.family); }
    out.rows.push(row);
  });
  return out;
}
